import { getAddressDisplayFields } from "utils/addressUtils";

import { Icon } from "@/design-system";

/**
 * One selectable address tile in the checkout's Ship to / Bill to pickers, on
 * the reference's address-book radio-row language (grep `shipAddrs` in
 * `Dens N Dente Tailwind.dc.html`): 1px border, selection = `border-blue-600` +
 * `bg-blue-50` with the LEADING icon swapping `location_on` → filled blue
 * `check_circle` — there is no trailing check. Blue is deliberate: the orange
 * chosen-row dress belongs to the Delivery option and Payment tabs; the
 * address book is a radio group and wears radio blue.
 *
 * **Selection ≠ default.** Selecting picks the address for THIS order; the
 * blue "Default" pill and the top-right "Set as default" action are about
 * FUTURE orders. `onSetDefault` is optional — omit it and the tile stays a
 * plain picker. Remove is an app adaptation (the reference's book has no
 * remove); it and "Set as default" stop propagation so they don't also select.
 */
export default function AddressCard({
  address,
  isSelected,
  isDefault = false,
  onSelect,
  onRemove,
  onSetDefault,
}) {
  const fields = getAddressDisplayFields(address);
  if (!fields) return null;

  const { name, streetLines, cityStateZip, countryDisplay, phone } = fields;

  return (
    <div
      role="radio"
      tabIndex={0}
      aria-checked={isSelected}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
      className={`flex cursor-pointer items-start gap-[14px] rounded-md border px-[var(--space-4)] py-[14px] text-left [transition:border-color_var(--duration-fast),background_var(--duration-fast)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] ${
        isSelected
          ? "border-blue-600 bg-blue-50"
          : "border-line-subtle bg-[var(--neutral-50)] hover:border-line"
      }`}
    >
      <Icon
        name={isSelected ? "check_circle" : "location_on"}
        size={22}
        fill={isSelected}
        className={`mt-[2px] shrink-0 ${isSelected ? "text-blue-600" : "text-ink-3"}`}
      />
      <div className="min-w-0 flex-1 text-sm">
        {(name || isDefault) && (
          <div className="flex items-center gap-[8px]">
            {name && <span className="font-semibold">{name}</span>}
            {isDefault && (
              <span className="rounded-full bg-blue-50 px-[8px] py-[2px] text-2xs font-bold uppercase tracking-[var(--tracking-wider)] text-blue-700">
                Default
              </span>
            )}
          </div>
        )}
        {streetLines && (
          <div className="whitespace-pre-line text-ink-2">{streetLines}</div>
        )}
        {cityStateZip && <div className="text-ink-2">{cityStateZip}</div>}
        {countryDisplay && <div className="text-ink-2">{countryDisplay}</div>}
        {phone && <div className="text-ink-2">{phone}</div>}
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
          className="mt-[var(--space-2)] inline-flex cursor-pointer items-center gap-[4px] rounded-sm border-0 bg-transparent p-0 font-sans text-xs font-semibold text-ink-3 [transition:color_var(--duration-fast)] hover:text-error-600"
        >
          <Icon name="delete" size={14} />
          Remove
        </button>
      </div>
      {!isDefault && onSetDefault && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onSetDefault();
          }}
          className="mt-[3px] shrink-0 cursor-pointer border-0 bg-transparent p-0 font-sans text-xs font-semibold text-ink-link hover:underline"
        >
          Set as default
        </button>
      )}
    </div>
  );
}
